import React, { useEffect, useMemo, useState } from "react";
import VirtualList from "../ui/VirtualList";
import {
  jobKey,
  jobName,
  jobStaff,
  rollupRoots,
  searchHierarchy,
} from "./hierarchyModel";

const ROOT_ID = "__root__";
const TREE_JOB_LIMIT = 6;
const ROW_HEIGHT = 36;
const SEARCH_DEBOUNCE_MS = 150;

// Shrink the label ~0.5px per nesting level (floored so it stays readable).
const labelSize = (depth) => `${Math.max(11, 14 - depth * 0.5)}px`;

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Highlight the matched substring in a label during search.
const Highlight = ({ text, query }) => {
  const q = query.trim().toLowerCase();
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q);
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

// Dotted vertical rails, one per ancestor level, to keep indentation legible.
const Guides = ({ depth }) =>
  depth > 0 ? (
    <span className="hv-guides" aria-hidden="true">
      {Array.from({ length: depth }).map((_, i) => (
        <span key={i} className="hv-guide" />
      ))}
    </span>
  ) : null;

// The indented-outline view — stays readable at any company size. Departments,
// sub-functions and jobs are all rows; long job lists collapse behind
// "show N more"; search filters and highlights across functions and jobs.
// Rows are built as data and only the ones in view are mounted.
const HierarchyTreeView = ({ hierarchy, selectedCompany }) => {
  const [expanded, setExpanded] = useState(() => new Set([ROOT_ID]));
  const [query, setQuery] = useState("");
  const [jobsOpen, setJobsOpen] = useState({});
  // Selected row + its parent row (for the highlight/glow). `tick` bumps on each
  // click so the parent's glow animation replays even for the same parent.
  const [selected, setSelected] = useState(null); // { key, parentKey }
  const [selectTick, setSelectTick] = useState(0);
  const selectItem = (key, parentKey) => {
    setSelected({ key, parentKey });
    setSelectTick((t) => t + 1);
  };

  const root = useMemo(() => {
    const roots = hierarchy?.roots || [];
    const { subs, jobs } = rollupRoots(roots);
    return {
      function_id: ROOT_ID,
      name: selectedCompany?.name || "Company",
      children: roots,
      jobs: [],
      _subs: subs,
      _jobs: jobs,
      isCompany: true,
    };
  }, [hierarchy, selectedCompany]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleJobs = (id) =>
    setJobsOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  const expandDepartments = () => {
    const next = new Set([ROOT_ID]);
    (hierarchy?.roots || []).forEach((n) => next.add(n.function_id));
    setExpanded(next);
  };
  const collapseAll = () => setExpanded(new Set([ROOT_ID]));

  const q = useDebounced(query, SEARCH_DEBOUNCE_MS).trim().toLowerCase();
  const matches = useMemo(() => searchHierarchy(root.children, q), [root, q]);

  // Visible rows, in order.
  const rows = useMemo(() => {
    const out = [];
    if (matches && matches.size === 0 && !root.name.toLowerCase().includes(q)) {
      return out;
    }
    const walk = (node, depth, parentKey) => {
      const match = matches && !node.isCompany ? matches.get(node.function_id) : null;
      if (matches && !node.isCompany && !match) return;
      const id = node.function_id;
      const key = String(id);
      const hasKids = node.children.length > 0 || node.jobs.length > 0;
      const open = matches ? true : expanded.has(id);
      out.push({ type: "node", key, id, node, depth, parentKey, hasKids, open });
      if (!open) return;

      node.children.forEach((c) => walk(c, depth + 1, key));

      const js = matches ? match?.jobs || [] : node.jobs;
      const showAll = Boolean(matches) || jobsOpen[key];
      const shown = showAll ? js : js.slice(0, TREE_JOB_LIMIT);
      shown.forEach((j, i) => {
        out.push({ type: "job", key: `job-${key}-${jobKey(j, i)}`, job: j, depth: depth + 1, parentKey: key });
      });
      if (!matches && js.length > TREE_JOB_LIMIT) {
        out.push({
          type: "more",
          key: `more-${key}`,
          fnKey: key,
          depth: depth + 1,
          remaining: js.length - TREE_JOB_LIMIT,
          opened: Boolean(jobsOpen[key]),
        });
      }
    };
    walk(root, 0, null);
    return out;
  }, [root, matches, q, expanded, jobsOpen]);

  const matchedJobCount = useMemo(() => {
    if (!matches) return 0;
    let n = 0;
    matches.forEach((m) => { n += m.jobs.length; });
    return n;
  }, [matches]);

  const renderRow = (row, _index, style) => {
    if (row.type === "more") {
      return (
        <div key={row.key} className="hv-tree-row" style={style}>
          <Guides depth={row.depth} />
          <span className="hv-twist spacer" />
          <button
            type="button"
            className="hv-show-more"
            onClick={(e) => {
              e.stopPropagation();
              toggleJobs(row.fnKey);
            }}
          >
            {row.opened ? "Show fewer" : `Show ${row.remaining} more`}
          </button>
        </div>
      );
    }

    if (row.type === "job") {
      const staff = jobStaff(row.job);
      return (
        <div
          key={row.key}
          className={`hv-tree-row job clickable${selected?.key === row.key ? " is-selected" : ""}`}
          style={style}
          onClick={() => selectItem(row.key, row.parentKey)}
        >
          <Guides depth={row.depth} />
          <span className="hv-twist spacer" />
          <span className="hv-rc-ic job">
            <i className="fa fa-briefcase" aria-hidden="true" />
          </span>
          <span className="hv-tree-label" style={{ fontSize: labelSize(row.depth) }}>
            <Highlight text={jobName(row.job)} query={q} />
          </span>
          {staff > 0 && (
            <span className="hv-tree-counts">
              <span className="hv-count-tag staff" title="Staff count">
                <i className="fa fa-users" aria-hidden="true" /> {staff}
              </span>
            </span>
          )}
        </div>
      );
    }

    const { node, key, depth, hasKids, open } = row;
    const iconClass =
      node.isCompany ? "fa fa-building"
      : node.children.length ? "fa fa-sitemap"
      : "fa fa-cube";
    const icKind = node.isCompany || node.children.length ? "dept" : "fn";
    const isSelected = selected?.key === key;
    const isParent = selected?.parentKey === key;

    return (
      <div
        // Remount the parent row on each selection so its glow animation replays.
        key={isParent ? `node-${key}-glow${selectTick}` : `node-${key}`}
        className={`hv-tree-row clickable${isSelected ? " is-selected" : ""}${
          isParent ? " is-parent" : ""
        }`}
        style={style}
        onClick={() => {
          if (hasKids) toggle(row.id);
          selectItem(key, row.parentKey);
        }}
      >
        <Guides depth={depth} />
        <span className={`hv-twist ${hasKids ? (open ? "open" : "") : "spacer"}`}>
          <i className="fa fa-caret-right" aria-hidden="true" />
        </span>
        <span className={`hv-rc-ic ${icKind}`}>
          <i className={iconClass} aria-hidden="true" />
        </span>
        <span className="hv-tree-label" style={{ fontSize: labelSize(depth) }}>
          <Highlight text={node.name} query={q} />
        </span>
        <span className="hv-tree-counts">
          {node._subs > 0 && (
            <span className="hv-count-tag">{node._subs} sub</span>
          )}
          {node._jobs > 0 && (
            <span className="hv-count-tag jobs">
              {node._jobs} job{node._jobs === 1 ? "" : "s"}
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="hv-panel">
      <div className="hv-tree-tools">
        <label className="hv-search">
          <i className="fa fa-search" aria-hidden="true" />
          <input
            type="text"
            value={query}
            placeholder="Search functions or jobs…"
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="hv-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          )}
        </label>
        {matches && (
          <span className="hv-search-count">
            {matches.size.toLocaleString()} function{matches.size === 1 ? "" : "s"} ·{" "}
            {matchedJobCount.toLocaleString()} job{matchedJobCount === 1 ? "" : "s"}
          </span>
        )}
        <button type="button" className="hv-mini-btn" onClick={expandDepartments}>
          Expand departments
        </button>
        <button type="button" className="hv-mini-btn" onClick={collapseAll}>
          Collapse all
        </button>
      </div>

      {rows.length > 0 ? (
        <VirtualList
          className="hv-tree"
          items={rows}
          itemHeight={ROW_HEIGHT}
          padding={8}
          resetKey={q}
          renderItem={renderRow}
        />
      ) : (
        <div className="hv-tree">
          <div className="hv-empty">
            No functions or jobs match “{query}”.
          </div>
        </div>
      )}

      <div className="hv-legend hv-legend-keys">
        <span>
          <span className="hv-legend-ic dept">
            <i className="fa fa-building" aria-hidden="true" />
          </span>
          Company
        </span>
        <span>
          <span className="hv-legend-ic dept">
            <i className="fa fa-sitemap" aria-hidden="true" />
          </span>
          Department (has sub-functions)
        </span>
        <span>
          <span className="hv-legend-ic dept">
            <i className="fa fa-cube" aria-hidden="true" />
          </span>
          Function
        </span>
        <span>
          <span className="hv-legend-ic job">
            <i className="fa fa-briefcase" aria-hidden="true" />
          </span>
          Job
        </span>
        <span>
          <span className="hv-legend-ic muted">
            <i className="fa fa-caret-right" aria-hidden="true" />
          </span>
          Click a row to highlight its parent
        </span>
      </div>
    </div>
  );
};

export default HierarchyTreeView;
